/**
 * Cart upsell link — LOAM
 *
 * A single text link rendered directly under an eligible cart line item.
 *
 * States:
 * 1. One-time purchase → link offers Monthly Subscription
 * 2. Monthly Subscription → link offers Quarterly Subscription
 * 3. Quarterly Subscription → show nothing
 *
 * Once a customer clicks an upsell, no further upsells are shown
 * for the rest of the browser session.
 */

(function () {
  const MONTHLY_PLAN_NAME = 'Deliver every month';
  const QUARTERLY_PLAN_NAME = '3 Pouches Delivered Every 3 Months';

  const ENGAGED_KEY = 'loam_upsell_engaged';

  const MOUNT_SELECTOR = '.cart-subscription-upsell';

  /**
   * Each line item in the drawer renders its own mount div. Find the
   * one that belongs to this line:
   * 1. a data-key / data-line-key on the mount or an ancestor, if the
   *    theme provides one
   * 2. otherwise by position (one mount per cart line, same order as
   *    cart.items)
   */
  function findMount(line, index, totalLines) {
    // Primary: the theme's own line item (<li id="CartDrawer-Item-{key}">).
    // The link is appended under the price inside the details column.
    const li = document.getElementById('CartDrawer-Item-' + line.key);

    if (li) {
      return li.querySelector('.horizontal-product__details') || li;
    }

    // Fallback: optional <div class="cart-subscription-upsell"> mounts.
    const mounts = document.querySelectorAll(MOUNT_SELECTOR);
    const key = String(line.key);
    const attrs = ['data-line-key', 'data-key', 'data-cart-item-key', 'data-line-item-key'];

    for (let i = 0; i < mounts.length; i++) {
      for (let j = 0; j < attrs.length; j++) {
        const holder = mounts[i].closest('[' + attrs[j] + ']');
        if (holder && holder.getAttribute(attrs[j]) === key) {
          return mounts[i];
        }
      }
    }

    if (mounts.length === totalLines) {
      return mounts[index];
    }

    return null;
  }

  const LINK_CLASS = 'cart-upsell-link';

  const UPSELL_CONFIG = {
    // LOAM Prebiotic Fiber Formula — 30-Day Pouch
    '44082624888866': {
      mode: 'variant_swap',
      label: 'LOAM-30DAY-001',
      quarterlyVariantId: '44473620267042'
    },

    // LOAM Prebiotic Fiber Formula — Grab-and-Go Packs
    '44082624921634': {
      mode: 'variant_swap',
      label: 'LOAM-SACHET-001',
      quarterlyVariantId: '44473620299810'
    },

    // Wheat Free
    '46017735229474': {
      mode: 'variant_swap',
      label: 'LOAM-30DAY-10004-A',
      quarterlyVariantId: '46017735295010'
    },

    // LOAM Prebiotic Fibre Formula — 30-Day Pouch
    '50393842679842': {
      mode: 'variant_swap',
      label: 'CORE30POU001UK',
      quarterlyVariantId: '50393842745378'
    },

    // LOAM Prebiotic Fibre Formula — Grab-and-Go Packs
    '50393842712610': {
      mode: 'variant_swap',
      label: 'CORE30STK001UK',
      quarterlyVariantId: '50393842778146'
    }  
  };

  // Text around the savings amount (formatted in the customer's currency).
  const LINK_TEXT = {
    onetime: { prefix: 'Subscribe & save ', suffix: '', sub: '+ Free Shipping in US/UK' },
    monthly: { prefix: 'Go quarterly & save ', suffix: '', sub: '+ Free Welcome Kit' }
  };

  // Quarterly variant = this many one-time units in a single pack.
  const QUARTERLY_PACK_SIZE = 3;

  /**
   * GA4 click event. Uses gtag if present, otherwise pushes to
   * dataLayer (GTM). Never throws.
   */
  function trackClick(state, targetPlanName) {
    const params = {
      from_plan: state.type === 'onetime' ? 'one_time' : 'monthly',
      to_plan: state.type === 'onetime' ? 'monthly' : 'quarterly',
      target_plan_name: targetPlanName,
      product_handle: state.line.handle,
      variant_id: String(state.line.variant_id)
    };

    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'cart_upsell_click', params);
      } else {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(
          Object.assign({ event: 'cart_upsell_click' }, params)
        );
      }
    } catch (e) {}
  }

  const productCache = {};

  /**
   * Product JSON from the storefront, cached per handle. Under Shopify
   * Markets this returns prices in the customer's presentment currency.
   */
  function getProduct(handle) {
    if (!productCache[handle]) {
      productCache[handle] = fetch('/products/' + handle + '.js')
        .then(function (response) {
          if (!response.ok) {
            throw new Error('Unable to load product data: ' + response.status);
          }

          return response.json();
        })
        .catch(function (error) {
          delete productCache[handle];
          throw error;
        });
    }

    return productCache[handle];
  }

  /**
   * Engagement flag (sessionStorage).
   * Wrapped in try/catch because storage can throw in private
   * browsing or when blocked by the browser.
   */
  function hasEngaged() {
    try {
      return sessionStorage.getItem(ENGAGED_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  function markEngaged() {
    try {
      sessionStorage.setItem(ENGAGED_KEY, '1');
    } catch (e) {}
  }

  function clearEngaged() {
    try {
      sessionStorage.removeItem(ENGAGED_KEY);
    } catch (e) {}
  }

  const UPSELL_PROPERTY = '_cartSubscriptionUpsell';

  // change.js replaces a line's properties, so keep the existing ones.
  function upsellProperties(line, offer) {
    return Object.assign({}, line.properties, { [UPSELL_PROPERTY]: offer });
  }

  /**
   * True if the cart holds any LOAM product the widget knows about,
   * including the quarterly variants we swap to.
   */
  function cartHasTrackedLine(cart) {
    const quarterlyIds = Object.keys(UPSELL_CONFIG).map(function (id) {
      return String(UPSELL_CONFIG[id].quarterlyVariantId);
    });

    return cart.items.some(function (line) {
      const id = String(line.variant_id);
      return (
        Object.prototype.hasOwnProperty.call(UPSELL_CONFIG, id) ||
        quarterlyIds.indexOf(id) !== -1
      );
    });
  }

  function resolveStates(cart) {
    // Item removed from the cart: reset so a re-add sees the offer again.
    if (hasEngaged() && !cartHasTrackedLine(cart)) {
      clearEngaged();
    }

    // Customer already engaged this session — don't re-offer.
    if (hasEngaged()) {
      return [];
    }

    const states = [];

    cart.items.forEach(function (line, index) {
      const cfg = UPSELL_CONFIG[String(line.variant_id)];

      if (!cfg) {
        return;
      }

      const plan =
        line.selling_plan_allocation &&
        line.selling_plan_allocation.selling_plan;

      // One-time purchase
      if (!plan) {
        states.push({ type: 'onetime', line: line, cfg: cfg, index: index, total: cart.items.length });
        return;
      }

      // Any subscription that isn't Quarterly
      if (plan.name !== QUARTERLY_PLAN_NAME) {
        states.push({ type: 'monthly', line: line, cfg: cfg, index: index, total: cart.items.length });
      }

      // Already quarterly: nothing to offer
    });

    return states;
  }

  function findPlanId(product, planName) {
    let foundPlanId = null;

    (product.selling_plan_groups || []).forEach(function (group) {
      (group.selling_plans || []).forEach(function (plan) {
        if (plan.name === planName) {
          foundPlanId = plan.id;
        }
      });
    });

    if (!foundPlanId) {
      throw new Error(
        'Selling plan "' + planName + '" not found on product "' + product.handle + '"'
      );
    }

    return foundPlanId;
  }

  function getSellingPlanId(handle, planName) {
    return getProduct(handle).then(function (product) {
      return findPlanId(product, planName);
    });
  }

  function findAllocationPrice(variant, planId) {
    const alloc = (variant.selling_plan_allocations || []).find(function (a) {
      return a.selling_plan_id === planId;
    });

    if (!alloc) {
      throw new Error('No allocation for plan ' + planId + ' on variant ' + variant.id);
    }

    return alloc.price;
  }

  function findVariant(product, variantId) {
    const variant = product.variants.find(function (v) {
      return String(v.id) === String(variantId);
    });

    if (!variant) {
      throw new Error('Variant ' + variantId + ' not found on "' + product.handle + '"');
    }

    return variant;
  }

  /**
   * Savings in minor units (cents) for the offer, times line quantity.
   * - one-time → monthly: one-time price minus monthly plan price
   * - monthly → quarterly: N units at the customer's CURRENT plan price
   *   minus the quarterly pack price (the extra saving over what they
   *   already get)
   */
  function computeSavings(state) {
    return getProduct(state.line.handle).then(function (product) {
      const current = findVariant(product, state.line.variant_id);
      let perUnit;

      if (state.type === 'onetime') {
        const planId = findPlanId(product, MONTHLY_PLAN_NAME);
        perUnit = current.price - findAllocationPrice(current, planId);
      } else {
        const quarterly = findVariant(product, state.cfg.quarterlyVariantId);
        const planId = findPlanId(product, QUARTERLY_PLAN_NAME);
        const currentPlanId = state.line.selling_plan_allocation.selling_plan.id;
        perUnit =
          QUARTERLY_PACK_SIZE * findAllocationPrice(current, currentPlanId) -
          findAllocationPrice(quarterly, planId);
      }

      return perUnit * state.line.quantity;
    });
  }

  function formatMoney(cents, currency) {
    const amount = cents / 100;

    return new Intl.NumberFormat(document.documentElement.lang || undefined, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
      maximumFractionDigits: 2
    }).format(amount);
  }

  function clearLinks() {
    document.querySelectorAll('.' + LINK_CLASS).forEach(function (el) {
      el.remove();
    });
  }

  let renderToken = 0;

  /**
   * Work out the savings for every offer first, then draw. An offer whose
   * savings can't be computed (or isn't positive) is hidden rather than
   * shown with a wrong amount.
   */
  function render(states, currency) {
    const token = ++renderToken;

    return Promise.all(
      states.map(function (state) {
        return computeSavings(state)
          .then(function (cents) {
            state.savingsText = cents > 0 ? formatMoney(cents, currency) : null;
          })
          .catch(function (error) {
            console.error('[cart-upsell] Unable to compute savings:', error);
            state.savingsText = null;
          });
      })
    ).then(function () {
      // A newer refresh started while we were fetching: drop this one.
      if (token !== renderToken) {
        return;
      }

      draw(states, currency);
    });
  }

  function draw(states, currency) {
    // The theme replaces MiniCart HTML on refresh, but clear anyway
    // so we never stack duplicate links.
    clearLinks();

    states.forEach(function (state) {
      if (!state.savingsText) {
        return;
      }

      const mount = findMount(state.line, state.index, state.total);

      if (!mount) {
        return;
      }

      const wrapper = document.createElement('div');
      wrapper.className = LINK_CLASS;

      const button = document.createElement('button');
      button.type = 'button';
      button.className = LINK_CLASS + '__btn';
      button.textContent =
        LINK_TEXT[state.type].prefix + state.savingsText + LINK_TEXT[state.type].suffix;

      wrapper.appendChild(button);

      // Optional subtext line under the link (leave sub empty to omit).
      if (LINK_TEXT[state.type].sub) {
        const sub = document.createElement('p');
        sub.className = LINK_CLASS + '__sub';
        sub.textContent = LINK_TEXT[state.type].sub;
        wrapper.appendChild(sub);
      }

      mount.appendChild(wrapper);

      button.addEventListener('click', function () {
        // Guard against double clicks.
        if (button.disabled) {
          return;
        }

        button.disabled = true;
        button.textContent = 'Updating…';

        // Set BEFORE the upgrade so the post-upgrade re-render
        // doesn't immediately show the next offer.
        markEngaged();

        const targetPlanName =
          state.type === 'onetime' ? MONTHLY_PLAN_NAME : QUARTERLY_PLAN_NAME;

        trackClick(state, targetPlanName);

        upgradeLine(state.line, state.cfg, targetPlanName).catch(function (error) {
          console.error('[cart-upsell] Upgrade failed:', error);

          // Upgrade didn't happen, so let them try again.
          clearEngaged();
          draw(states, currency);
        });
      });
    });
  }

  function upgradeLine(line, cfg, targetPlanName) {

    const offer = targetPlanName === QUARTERLY_PLAN_NAME ? 'quarterly' : 'monthly';
    
    /**
     * Quarterly version:
     * remove current variant and add quarterly variant.
     */
    if (cfg.mode === 'variant_swap' && targetPlanName === QUARTERLY_PLAN_NAME) {
      return getSellingPlanId(line.handle, targetPlanName)
        .then(function (planId) {
          return fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: line.key, quantity: 0 })
          })
            .then(checkOk)
            .then(function () {
              return fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  items: [
                    {
                      id: cfg.quarterlyVariantId,
                      quantity: line.quantity,
                      selling_plan: planId,
                      properties: upsellProperties(line, offer)
                    }
                  ]
                })
              });
            });
        })
        .then(checkOk)
        .then(refreshDrawerAfterUpgrade);
    }

    /**
     * Normal LOAM behavior:
     * same variant, different selling plan.
     */
    return getSellingPlanId(line.handle, targetPlanName)
      .then(function (planId) {
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: line.key,
            quantity: line.quantity,
            selling_plan: planId,
            properties: upsellProperties(line, offer)
          })
        });
      })
      .then(checkOk)
      .then(refreshDrawerAfterUpgrade);
  }

  function checkOk(response) {
    if (!response.ok) {
      throw new Error('Cart request failed: ' + response.status);
    }

    return response;
  }

  /**
   * Re-evaluate the cart and re-render the links.
   *
   * This runs when:
   * - cart initially loads
   * - theme fires cart:updated
   */
  function refreshWidget() {
    return fetch('/cart.js')
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        return render(resolveStates(cart), cart.currency);
      })
      .catch(function (error) {
        console.error('[cart-upsell] Unable to refresh widget:', error);
      });
  }

  /**
   * After an upgrade:
   * 1. Tell the existing LOAM theme to refresh MiniCart
   * 2. Keep the drawer open
   * 3. Re-render our link after the new drawer markup exists
   */
  function refreshDrawerAfterUpgrade() {
    document.dispatchEvent(
      new CustomEvent('cart:refresh', { detail: { open: true } })
    );

    window.setTimeout(refreshWidget, 400);
  }

  /**
   * Initial render. Does NOT open the cart drawer.
   */
  refreshWidget();

  /**
   * Your LOAM theme already fires this event
   * after normal cart updates.
   */
  document.addEventListener('cart:updated', function () {
    window.setTimeout(refreshWidget, 50);
  });
})();
